import type { Palette } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { ColorsStorage as LegacyColorsStorage } from "@/Utils/Constant/ColorsStorage";

export enum ThemeType {
  LIGHT = "light",
  DARK = "dark",
}

const typography = {
  fontFamily: ["Montserrat", "Roboto", "sans-serif"].join(","),
};

const barChartColors = [
  "#469990",
  "#0067ED",
  "#00ADC4",
  "#EF058B",
  "#F56209",
  "#71AA3B",
  "#FF9E0C",
  "#532EA7",
  "#009FF2",
  "#E9386F",
  "#FF653B",
  "#686868",
  "#EF5056",
  "#6FBB60",
  "#4F90CE",
  "#F99D50",
  "#945CA1",
  "#C7654E",
  "#D174AA",
  "#A4C1B9",
  "#69DEDE",
  "#FFD8B1",
  "#FCA4DA",
  "#FFFAC8",
  "#BFF8EB",
  "#AAFFC3",
  "#DCBEFF",
  "#FABED4",
  "#C7D5E3",
  "#EBEBEB",
];

const CHAT_SPACING = {
  messageBubblePadding: 1.5,
  messageGap: 2.5,
  avatarGap: 1.5,
  componentMargin: 1,
  statusMargin: 1.5,
  agentStatusGap: 1,
};
const CHAT_BORDER_RADIUS = {
  messageBubble: 1.5,
  avatar: "50%",
  skeleton: 3,
  chip: 3,
};
const CHAT_AVATAR = { size: 30 };
const CHAT_MESSAGE_BUBBLE = {
  maxWidth: { xs: "85%", sm: "75%", md: "65%" },
  minWidth: "100px",
  agentStatusMinWidth: "200px",
  tail: { size: 8, offset: 12 },
};

const CHAT_ANIMATIONS = {
  pulse: {
    name: "maite-pulse",
    duration: "1.5s",
    keyframes: {
      "0%": { opacity: 1 },
      "50%": { opacity: 0.5 },
      "100%": { opacity: 1 },
    },
  },
  blink: {
    name: "maite-blink",
    duration: "1s",
    keyframes: {
      "0%": { opacity: 1 },
      "50%": { opacity: 1 },
      "51%": { opacity: 0 },
      "100%": { opacity: 0 },
    },
  },
  typingCursor: {
    width: "8px",
    height: "16px",
    marginLeft: 0.5,
  },
};

const createChatThemeTokens = (p: Palette) => ({
  spacing: CHAT_SPACING,
  borderRadius: CHAT_BORDER_RADIUS,
  avatar: CHAT_AVATAR,
  messageBubble: CHAT_MESSAGE_BUBBLE,
  animations: CHAT_ANIMATIONS,
  colors: {
    user: {
      light: {
        background: "#F6F9FD",
        text: "#2E2E2E",
        tail: p.primary.light,
      },
      dark: {
        background: "#2E2E2E",
        text: "#ffffff",
        tail: p.primary.dark ?? p.primary.main,
      },
    },
    assistant: {
      light: {
        background: "#F6F9FD",
        text: p.text?.primary ?? "#000000",
        border: p.divider ?? "#e0e0e0",
        tail: p.primary.light,
      },
      dark: {
        background: p.background.paper,
        text: p.text?.primary ?? "#ffffff",
        border: p.divider ?? (p.mode === "dark" ? "#616161" : "#e0e0e0"),
        tail: p.primary.dark ?? p.primary.main,
      },
    },
  },
  statusAlert: {
    warning: { light: "#FFF3E0", dark: "#2D2412" },
    info: { light: "#E3F2FD", dark: "#071B2F" },
  },
  chip: { agent: { fontSize: "0.75rem", height: "20px" } },
});

const commonComponentOverrides = {
  MuiButton: {
    defaultProps: { disableElevation: true },
  },
  MuiCssBaseline: {
    styleOverrides: `
      @keyframes ${CHAT_ANIMATIONS.pulse.name} {
        0% { opacity: 1; }
        50% { opacity: 0.5; }
        100% { opacity: 1; }
      }
      @keyframes ${CHAT_ANIMATIONS.blink.name} {
        0%, 50% { opacity: 1; }
        51%, 100% { opacity: 0; }
      }
    `,
  },
};

const lightTheme = createTheme({
  palette: {
    avatar: {
      background: "#F0F3FF",
      textColor: "#000000",
    },
    mode: ThemeType.LIGHT,
    tooltip: {
      background: "#f2f2f2",
      textColor: "#131313",
    },
    formCard: {
      headerBackground: "#F0F3FF",
      contentBackground: "#ffffff",
      border: "#e0e0e0",
    },
    echartTooltip: {
      background: "#ffffff",
      textColor: "#2E2E2E",
    },
    background: {
      paper: "#ffffff",
    },
    pill: {
      background: "#f3f4f6",
      textColor: "#000000",
    },
    chip: {
      background: "#E7F1FA",
      textColor: "#000000",
    },
    alertInfo: {
      background: "#E8DEF8",
      textColor: "#000000",
    },
    cardBorder: {
      background: "#0E22441A",
      textColor: "#000000",
    },
    section: {
      border: "#E5E5E5",
    },
    navigation: {
      border: "#E5E5E5",
      buttonBorder: "#DFE0E3",
    },
    brandedTypography: {
      textColor: "#0e2244",
    },
    toggleButton: {
      background: "#0e2244",
      textColor: "#FFF",
      hoverBackground: "#273D63",
      hoverTextColor: "#FFF",
    },
    success: {
      main: "#00A953",
      light: "#4CAF50",
      dark: "#388E3C",
    },
    warning: {
      main: "#FC9429",
      light: "#FFB74D",
      dark: "#F57C00",
    },
    spendRevenueGraph: {
      spend: {
        brand: "#0C4873",
        category: "#3B9BBA",
        product: "#4AFFFF",
      },
      revenue: {
        brand: "#8465C2",
        category: "#A97EFF",
        product: "#E68DFC",
      },
    },
    roiGraph: {
      layer: {
        brand: "#09A158",
        category: "#51DE9B",
        product: "#B1DDC8",
      },
    },
    datePicker: {
      selectedItem: "#0e2244",
      selectedItemText: "#FFF",
      selectedRange: "#F5F5F5",
      selectedRangeText: "#0e2244",
      borderColor: "#0e2244",
      presetCellHoverBg: "#0000000a",
    },
    textFieldDisabled: {
      background: "#f5f5f5",
      textColor: "#616161",
    },
    ChartGuideLines: {
      color: "#e0e0e0",
    },
    ROILineChart: {
      lineColor: "#EC0089",
      barChartColors: barChartColors,
    },
    SpendLineChart: {
      lineColor: "#09A856",
    },
    RevenueLineChart: {
      lineColor: "#795ABA",
    },
    adStockChart: {
      length: "#8884d8",
      peak: "#e07a5f",
    },
    channelEffectivenessChart: {
      marketingSpend: "#1F4690",
      marketingRevenue: "#9C27B0",
      shareOfEffect: "#009693",
      roi: "#4CAF50",
      marketingReturn: "#009650",
    },
    mutinexMagenta: {
      main: "#EC0089",
      contrastText: "#fff",
    },
    error: {
      main: "#EA463D",
      light: "#FAE9E9",
    },
    hyperLink: {
      textColor: "#0000EE",
    },
    link: {
      color: "#2E90E1",
    },
    banner: {
      background: "#F9FAFF",
    },
    selectableCard: {
      color: "#2E2E2E",
      backgroundColor: "#FFFFFF",
      hoverBackgroundColor: "#F8F9FF",
    },
    internalLink: {
      color: "#0023FA",
    },
    scenarioType: {
      forecast: "#F3F5FF",
      optimizer: "#DAF1E8",
      superOptimizer: "#FDF3EB",
      revenue: "#E6FFE6",
      unknown: "#F5F5F5",
    },
    tableColumnHeader: {
      background: "#EEF3FD",
    },
    statusColors: {
      approved: "#1EB35A", // Green
      archived: "#B3261E", // Red
      draft: "#FFB700", // Yellow
      completed: "#1F2937", // Dark blue/gray
      processing: "#2E98E5", // Blue
      error: "#B3261E", // Red
      unknown: "#6B7280", // Gray
    },
    activityLog: {
      user: "#6E6972",
    },
    bubbleChart: {
      baseText: "#FFFFFF",
      comparisonText: "#000000",
      dashedLine: "#6E6972",
      bubbleColors: LegacyColorsStorage.color.fill,
    },
    instantInsights: {
      background: "#F6F1FF",
    },
    infoBanner: {
      background: "#E5F2F9",
      lightBackground: "#F6F1FF",
    },
    infoIcon: {
      color: "#565EFA",
    },
    baseSales: {
      color: "#0E2244",
    },
    channelInputs: {
      border: "#e0e0e0",
      backgroundHover: "rgba(0, 0, 0, 0.04)",
      backgroundDisabled: "rgba(0, 0, 0, 0.03)",
    },
    breadcrumb: {
      currentPageBackground: "#f3f4f6",
      currentPageTextColor: "#374151",
    },
    businessUnitHierarchyHeader: {
      background: "#F9FAFB",
      textColor: "#000000",
    },
    slider: {
      pathColor: "#E5F2F9",
    },
    quickSimulate: {
      backgroundColor: "#FFFFFF",
    },
    scenarioSummary: {
      backgroundColor: "#F0F3FF",
    },
    logo: {
      fillColor: "#0F2144",
      strokeColor: "#0F2144",
    },
    primary: { main: "#101F3B" },
    secondary: { main: "#F5F5F5" },
    active: { main: "#EBF3FB", contrastText: "#000" },
    actionPlans: {
      ctaBackground: "#EEF3FD",
    },
  },
  typography,
  components: commonComponentOverrides,
});
lightTheme.chatTheme = createChatThemeTokens(lightTheme.palette);

const darkTheme = createTheme({
  palette: {
    avatar: {
      background: "#F0F3FF",
      textColor: "#000000",
    },
    mode: ThemeType.DARK,
    tooltip: {
      background: "#2f2f2f",
      textColor: "#ffffff",
    },
    formCard: {
      headerBackground: "rgba(45, 55, 72, 0.65)",
      contentBackground: "#2f2f2f",
      border: "#3e3e3e",
    },
    echartTooltip: {
      background: "#2E2E2E",
      textColor: "#ffffff",
    },
    background: {
      paper: "#2f2f2f",
    },
    pill: {
      background: "#383838",
      textColor: "#000000",
    },
    chip: {
      background: "#212A32",
      textColor: "#FFFFFF",
    },
    alertInfo: {
      background: "#7C70B5",
      textColor: "#FFF",
    },
    cardBorder: {
      background: "#eaf0e9",
      textColor: "#000000",
    },
    section: {
      border: "#E5E5E5",
    },
    navigation: {
      border: "#656565",
      buttonBorder: "#656565",
    },
    brandedTypography: {
      textColor: "#FFF",
    },
    linkTypography: {
      textColor: "#61A9FF",
    },
    toggleButton: {
      background: "#FFF",
      textColor: "#000",
      hoverBackground: "#D3D3D3",
      hoverTextColor: "#000",
    },
    success: {
      main: "#34D476",
      light: "#66BB6A",
      dark: "#2E7D32",
    },
    warning: {
      main: "#FFB74D",
      light: "#FFCC80",
      dark: "#F57C00",
    },
    spendRevenueGraph: {
      spend: {
        brand: "#56A3D8",
        category: "#6EC6E7",
        product: "#7ED3FF",
      },
      revenue: {
        brand: "#B89EE7",
        category: "#D0B4FF",
        product: "#EFD4FF",
      },
    },
    roiGraph: {
      layer: {
        brand: "#057533",
        category: "#2B7B4F",
        product: "#5B7E6D",
      },
    },
    datePicker: {
      selectedItem: "#FFF",
      selectedItemText: "#000",
      selectedRange: "#242A36",
      selectedRangeText: "#FFF",
      borderColor: "#F5F5F5",
      presetCellHoverBg: "#434343",
    },
    textFieldDisabled: {
      background: "#616161",
      textColor: "#f5f5f5",
    },
    ChartGuideLines: {
      color: "#616161",
    },
    ROILineChart: {
      lineColor: "#EC0089",
      barChartColors: barChartColors,
    },
    SpendLineChart: {
      lineColor: "#09A856",
    },
    RevenueLineChart: {
      lineColor: "#795ABA",
    },
    adStockChart: {
      length: "#8884d8",
      peak: "#e07a5f",
    },
    channelEffectivenessChart: {
      marketingSpend: "#1F4690",
      marketingRevenue: "#9C27B0",
      shareOfEffect: "#009693",
      roi: "#4CAF50",
      marketingReturn: "#009650",
    },
    mutinexMagenta: {
      main: "#EC0089",
      contrastText: "#fff",
    },
    error: {
      main: "#EA463D",
      light: "#FAE9E9",
    },
    hyperLink: {
      textColor: "#00C0FF",
    },
    link: {
      color: "#8694FC",
    },
    banner: {
      background: "#515151",
    },
    infoBanner: {
      background: "#2A3A4B",
      lightBackground: "#4F4F4F",
    },
    infoIcon: {
      color: "#FFFFFF",
    },
    selectableCard: {
      color: "#FFFFFF",
      backgroundColor: "#2E2E2E",
      hoverBackgroundColor: "#2E2E2E",
    },
    internalLink: {
      color: "#8694FC",
    },
    scenarioType: {
      forecast: "#D6DDFF",
      optimizer: "#7DFFCC",
      revenue: "#E6FFE6",
      superOptimizer: "#FFDEC4",
      unknown: "#F5F5F5",
    },
    tableColumnHeader: {
      background: "#313131",
    },
    statusColors: {
      approved: "#34D476", // Lighter green
      archived: "#FF4D4D", // Lighter red
      draft: "#FFD24D", // Lighter yellow
      completed: "#FFF", // White
      processing: "#4DB8FF", // Lighter blue
      error: "#FF4D4D", // Lighter red
      unknown: "#9CA3AF", // Lighter gray
    },
    activityLog: {
      user: "#9CA3AF",
    },
    bubbleChart: {
      baseText: "#000000",
      comparisonText: "#FFFFFF",
      dashedLine: "#EBEBEB",
      bubbleColors: LegacyColorsStorage.color.fill,
    },
    instantInsights: {
      background: "#4F4F4F",
    },
    baseSales: {
      color: "#F6F1FF",
    },
    channelInputs: {
      border: "#3e3e3e",
      backgroundHover: "rgba(255, 255, 255, 0.08)",
      backgroundDisabled: "rgba(255, 255, 255, 0.03)",
    },
    breadcrumb: {
      currentPageBackground: "#4B5563",
      currentPageTextColor: "#F9FAFB",
    },
    businessUnitHierarchyHeader: {
      background: "#1F2937",
      textColor: "#FFFFFF",
    },
    slider: {
      pathColor: "#374151",
    },
    quickSimulate: {
      backgroundColor: "#242423",
    },
    scenarioSummary: {
      backgroundColor: "#2E2E2E",
    },
    logo: {
      fillColor: "#FFFFFF",
      strokeColor: "#FFFFFF",
    },
    primary: { main: "#ffffff" },
    secondary: { main: "#343434" },
    active: { main: "#263039", contrastText: "#fff" },
    actionPlans: {
      ctaBackground: "#2B3033",
    },
  },
  typography,
  components: commonComponentOverrides,
});
darkTheme.chatTheme = createChatThemeTokens(darkTheme.palette);

export const theme = (themeType: ThemeType) =>
  themeType === ThemeType.LIGHT ? lightTheme : darkTheme;

export const hexToRGBA = (hex: string, alpha = 1) => {
  hex = hex.startsWith("#") ? hex.slice(1) : hex;
  if (hex.length === 3) {
    hex = Array.from(hex).reduce((str, x) => str + x + x, "");
  }

  const values = hex
    .split(/([a-zA-Z0-9]{2})/)
    .filter(Boolean)
    .map((x) => parseInt(x, 16));
  return `rgba(${values.join(", ")}, ${alpha})`;
};

export const generateColorPalette = (numChannels: number, baseColors: string[]) => {
  return Array.from({ length: numChannels }, (_, i) => baseColors[i % baseColors.length]);
};

export default theme;
